import {
  Avatar,
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Text
} from '@chakra-ui/react';
import { CMXIcon, LoadingSlugs } from '@codametrix/ui-components';
import React from 'react';

import type { SiblingCaseProvider } from '../../interface/chip';
import type {
  ProviderDirectoryEntry,
  ProviderDirectorySpecialty
} from '../../interface/providerDirectory';
import {
  CHIP_COLORS,
  INK,
  INK_MUTED,
  POPOVER_BORDER,
  PROVIDER_DRAWER_GEOMETRY,
  SIBLING_ROW_COLORS,
  SURFACE_WARM
} from '../../styles/tokens';

const INTER_STACK = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const isNonEmptyString = (v: unknown): v is string =>
  typeof v === 'string' && v.trim() !== '';

const na = (v: unknown): string =>
  isNonEmptyString(v) || typeof v === 'number' ? String(v) : 'N/A';

const providerLabel = (p: { readonly name: string; readonly credential?: string }): string =>
  p.credential ? `${p.name}, ${p.credential}` : p.name;

/** One "vertical-info" label/value block in the provider-details drawer's header -- mirrors
 *  provider-details.tsx's own `.vertical-info` (uppercase overline label above a value), reused for
 *  both "ROLE" and "EHR ID". */
const ProviderInfoBlock: React.FC<{
  readonly testId: string;
  readonly label: string;
  readonly value: string;
}> = ({ testId, label, value }) => (
  <Flex direction="column" data-testid={testId}>
    <Text
      as="span"
      fontSize="8.5px"
      fontWeight={700}
      textTransform="uppercase"
      letterSpacing="0.06em"
      color={INK_MUTED}
    >
      {label}
    </Text>
    <Text as="span" fontSize="11px" fontWeight={600} color={INK}>
      {value}
    </Text>
  </Flex>
);

/** Groups `items` by `key(item)`, preserving first-seen key order -- `objectUtils.groupBy` isn't on
 *  the isolation allowlist, so this is the small local equivalent the rest of this file already
 *  favors. */
const groupBy = <T,>(items: readonly T[], key: (item: T) => string): Map<string, T[]> => {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const k = key(item);
    const existing = groups.get(k);
    if (existing) {
      existing.push(item);
    } else {
      groups.set(k, [item]);
    }
  }
  return groups;
};

/** One SPECIALTY chip pill -- same visual language as `DiagnosisChip`'s resting (non-shared) treatment,
 *  minus the monospace/code styling (a specialty name is prose, not a code). */
const SpecialtyChip: React.FC<{ readonly specialty: string }> = ({ specialty }) => (
  <Text
    as="span"
    fontSize="10.5px"
    fontWeight={600}
    color={SIBLING_ROW_COLORS.roleText}
    bg={SIBLING_ROW_COLORS.panel2}
    borderWidth="1px"
    borderStyle="solid"
    borderColor={POPOVER_BORDER}
    borderRadius="3px"
    px="5px"
    py="1px"
  >
    {specialty}
  </Text>
);

/** One role group: the role name (`specialties[x].role`) as a heading, directly above a SINGLE row
 *  of SPECIALTY chips, SPECIALTY STATUS, and BILLABLE? -- mirrors `provider-details.tsx`'s own
 *  per-role `.flex-box` row (`isRoleBillable` = ANY specialty in the group is billable; unique
 *  non-empty status indicators joined, "None" when there are none). No separate "Role" column: the
 *  heading already says it, the same way the real UI's own row heading (`provider-location`, shown
 *  as `name` instead of `location` once the search is scoped to one facility) never repeats it as a
 *  column either. */
const ProviderRoleGroup: React.FC<{
  readonly role: string;
  readonly specialties: readonly ProviderDirectorySpecialty[];
}> = ({ role, specialties }) => {
  const isBillable = specialties.some((s) => s.billable);
  const statuses = Array.from(
    new Set(specialties.map((s) => s.statusIndicator).filter(isNonEmptyString))
  );
  return (
    <Box data-testid="encounter-provider-role-row">
      <Text
        data-testid="encounter-provider-role-name"
        fontSize="11.5px"
        fontWeight={700}
        color={INK}
        mb="4px"
      >
        {role}
      </Text>
      <Flex gap="14px">
        <Flex direction="column" data-testid="encounter-provider-specialty">
          <Text
            as="span"
            fontSize="8.5px"
            fontWeight={700}
            textTransform="uppercase"
            letterSpacing="0.06em"
            color={INK_MUTED}
          >
            Specialty
          </Text>
          <Flex wrap="wrap" gap="4px" mt="2px">
            {specialties
              .filter((s) => isNonEmptyString(s.specialty))
              .map((s, i) => (
                <SpecialtyChip key={i} specialty={s.specialty as string} />
              ))}
          </Flex>
        </Flex>
        <ProviderInfoBlock
          testId="encounter-provider-specialty-status"
          label="Specialty status"
          value={statuses.length > 0 ? statuses.join(', ') : 'None'}
        />
        <Flex direction="column" data-testid="encounter-provider-billable">
          <Text
            as="span"
            fontSize="8.5px"
            fontWeight={700}
            textTransform="uppercase"
            letterSpacing="0.06em"
            color={INK_MUTED}
          >
            Billable?
          </Text>
          <Box mt="2px">
            {isBillable ? (
              <CMXIcon svgTitleText="Billable" svgIconName="BiomeCheckCircle" svgIconSize="14" />
            ) : (
              <CMXIcon svgTitleText="Not billable" svgIconName="BiomeNoCircle" svgIconSize="14" />
            )}
          </Box>
        </Flex>
      </Flex>
    </Box>
  );
};

/** The drawer body's populated view: `specialties` grouped by `location` FIRST, then by `role`
 *  WITHIN each location -- same two-level nesting `provider-details.tsx`'s own
 *  `specialtiesByLoc`/`specialtiesByRole` uses, so two same-named roles at DIFFERENT facilities
 *  render as separate rows instead of incorrectly merging. `location` itself is never shown as its
 *  own heading text (FACILITY already shows once, synchronously, in the header, off
 *  `EncounterData.facility`) -- each row's heading is always the role name, mirroring the real UI's
 *  own `isSingleTenantData` branch (`isSingleTenantData ? name : location`), since our single
 *  top-level FACILITY value is exactly that "one facility" framing. */
const ProviderSpecialtyGroups: React.FC<{
  readonly specialties: readonly ProviderDirectorySpecialty[];
}> = ({ specialties }) => {
  const byLocation = groupBy<ProviderDirectorySpecialty>(specialties, (s) => s.location ?? '');
  return (
    <Flex direction="column" gap="10px" data-testid="encounter-provider-specialty-groups">
      {Array.from(byLocation.entries()).map(([location, locationSpecialties]) => {
        const byRole = groupBy<ProviderDirectorySpecialty>(locationSpecialties, (s) => s.role);
        return Array.from(byRole.entries()).map(([role, roleSpecialties]) => (
          <ProviderRoleGroup key={`${location}|${role}`} role={role} specialties={roleSpecialties} />
        ));
      })}
    </Flex>
  );
};

export interface ProviderDrawerProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly finalFocusRef: React.RefObject<HTMLElement>;
  readonly selectedProvider: {
    readonly role: string;
    readonly provider: SiblingCaseProvider;
  };
  readonly facility: string | undefined;
  readonly providerFetch: 'idle' | 'loading' | 'success' | 'error';
  readonly providerDetails: ProviderDirectoryEntry | undefined;
}

/** The provider-details drawer opened from a "FINAL SIGNING PROVIDER"/"AUTHORING PROVIDER" link on
 *  an `Encounter` sibling-case row -- presentation only, all fetch/selection state is owned by
 *  `Encounter`. */
export const ProviderDrawer: React.FC<ProviderDrawerProps> = ({
  isOpen,
  onClose,
  finalFocusRef,
  selectedProvider,
  facility,
  providerFetch,
  providerDetails
}) => (
  <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="lg" finalFocusRef={finalFocusRef}>
    <DrawerOverlay />
    <DrawerContent data-testid="encounter-provider-dialog" bg={SURFACE_WARM} fontFamily={INTER_STACK}>
      <DrawerHeader
        data-testid="encounter-provider-dialog-header"
        px={PROVIDER_DRAWER_GEOMETRY.headerX}
        pt={PROVIDER_DRAWER_GEOMETRY.headerTop}
        pb={PROVIDER_DRAWER_GEOMETRY.headerBottom}
      >
        <Box
          borderBottomWidth="1px"
          borderColor={POPOVER_BORDER}
          pb={PROVIDER_DRAWER_GEOMETRY.headerContentBottom}
        >
          <Flex direction="column" gap="10px">
            <Text
              as="span"
              data-testid="encounter-provider-dialog-role"
              fontSize="8.5px"
              fontWeight={700}
              textTransform="uppercase"
              letterSpacing="0.06em"
              color={INK_MUTED}
            >
              {selectedProvider.role}
            </Text>
            <Flex align="center" gap="8px">
              <Avatar
                name={selectedProvider.provider.name}
                size="sm"
                bg={CHIP_COLORS.primary}
                color={CHIP_COLORS.surface}
              />
              <Text data-testid="encounter-provider-dialog-name" fontSize="14px" fontWeight={700} color={INK}>
                {providerLabel(selectedProvider.provider)}
              </Text>
            </Flex>
            <Flex gap="20px" wrap="wrap">
              <ProviderInfoBlock
                testId="encounter-provider-dialog-ehr-id"
                label="EHR ID"
                value={na(selectedProvider.provider.number)}
              />
              {providerFetch === 'success' && isNonEmptyString(providerDetails?.employerStatus) ? (
                <ProviderInfoBlock
                  testId="encounter-provider-dialog-employer"
                  label="Employer"
                  value={providerDetails.employerStatus}
                />
              ) : null}
              {isNonEmptyString(facility) ? (
                <ProviderInfoBlock testId="encounter-provider-dialog-facility" label="Facility" value={facility} />
              ) : null}
            </Flex>
          </Flex>
        </Box>
      </DrawerHeader>
      <DrawerCloseButton
        aria-label="Close"
        top={PROVIDER_DRAWER_GEOMETRY.closeButtonInset}
        insetEnd={PROVIDER_DRAWER_GEOMETRY.closeButtonInset}
      />
      <DrawerBody
        px={PROVIDER_DRAWER_GEOMETRY.bodyX}
        pt={PROVIDER_DRAWER_GEOMETRY.bodyTop}
        pb={PROVIDER_DRAWER_GEOMETRY.bodyBottom}
      >
        {providerFetch === 'loading' ? (
          <Box data-testid="encounter-provider-dialog-loading">
            <LoadingSlugs numberItems={3} />
          </Box>
        ) : providerFetch === 'error' ? (
          <Text data-testid="encounter-provider-dialog-error" fontSize="12px" color={CHIP_COLORS.warn}>
            Couldn't load additional provider details.
          </Text>
        ) : providerDetails && providerDetails.specialties.length > 0 ? (
          <ProviderSpecialtyGroups specialties={providerDetails.specialties} />
        ) : (
          <Text data-testid="encounter-provider-dialog-empty" fontSize="12px" color={INK_MUTED} fontStyle="italic">
            No additional details available for this provider.
          </Text>
        )}
      </DrawerBody>
    </DrawerContent>
  </Drawer>
);
